import { useRef, useState, useCallback } from 'react';
import type { FlatWbsRow, InlineCreateState, WbsTreeNode } from './types';
import type { ProjectMember } from '../../services/projectService';
import WbsTable from './WbsTable';
import WbsGanttChart from './WbsGanttChart';

interface Props {
  wbsData: WbsTreeNode[];
  flattenedRows: FlatWbsRow[];
  timeUnit: 'day' | 'week' | 'month';
  bufferEnabled: boolean;
  members: ProjectMember[];
  inlineCreate: InlineCreateState | null;
  onToggle: (wbsItemId: string) => void;
  onFieldUpdate: (wbsItemId: string, field: string, value: unknown) => void;
  onStartInlineCreate: (parentId: string | null, depth: number) => void;
  onDelete: (wbsItemId: string, title: string, childCount: number) => void;
  onInlineSubmit: (data: { parentId: string | null; title: string }) => void;
  onInlineCancel: () => void;
}

const MIN_TITLE_WIDTH = 160;
const MAX_TITLE_WIDTH = 600;

export default function WbsBody({
  wbsData,
  flattenedRows,
  timeUnit,
  bufferEnabled,
  members,
  inlineCreate,
  onToggle,
  onFieldUpdate,
  onStartInlineCreate,
  onDelete,
  onInlineSubmit,
  onInlineCancel,
}: Props) {
  const ganttScrollRef = useRef<HTMLDivElement>(null);
  const [titleColWidth, setTitleColWidth] = useState(240);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = titleColWidth;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientX - startX;
        const newWidth = Math.min(MAX_TITLE_WIDTH, Math.max(MIN_TITLE_WIDTH, startWidth + delta));
        setTitleColWidth(newWidth);
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [titleColWidth],
  );

  return (
    <div className="flex flex-1 overflow-hidden border-t">
      {/* 좌측 WBS 테이블 */}
      <WbsTable
        flattenedRows={flattenedRows}
        bufferEnabled={bufferEnabled}
        members={members}
        inlineCreate={inlineCreate}
        titleColWidth={titleColWidth}
        hoveredRowId={hoveredRowId}
        onHoverRow={setHoveredRowId}
        onResizeStart={handleResizeStart}
        onToggle={onToggle}
        onFieldUpdate={onFieldUpdate}
        onStartInlineCreate={onStartInlineCreate}
        onDelete={onDelete}
        onInlineSubmit={onInlineSubmit}
        onInlineCancel={onInlineCancel}
      />

      {/* 우측 Gantt 차트 */}
      <WbsGanttChart
        wbsData={wbsData}
        flattenedRows={flattenedRows}
        timeUnit={timeUnit}
        scrollRef={ganttScrollRef}
        hoveredRowId={hoveredRowId}
        onHoverRow={setHoveredRowId}
        onFieldUpdate={onFieldUpdate}
      />
    </div>
  );
}
